import axios from "axios";
import { getEnv, getWindow, notifyError } from "../../effects-bridge";
import store from "../../redux-store";
import { ENDPOINT } from "../../config/endpoints";

/**
 * Uplifted from `apps/web/src/helper/feedback-helper.ts`. Web's body, shared by both apps since 2026-10-27
 * (RESERVATIONS_V2_PLAN.md RV5d: Reservations v2's interest page sends the same enquiry on Android and iOS,
 * approved by the user). Mobile had no copy of its own, so this is one body, not a convergence: both variants
 * re-export it. Mobile's env bridge carries `dotpeApiUrl` for it.
 *
 * Three app-side couplings became seams:
 *   - `process.env.REACT_APP_DOTPE_API_URL` -> `getEnv("dotpeApiUrl")`. Webpack INLINES
 *     `process.env.REACT_APP_*` at build time, so a shared module genuinely cannot read it.
 *   - `ToastNotifyError` -> `notifyError` (UI).
 *   - `window.location.href` -> `getWindow()?.location?.href`, optional-chained because mobile
 *     passes `globalThis`, which has no `location`. This module is web-only today, but the
 *     bridge contract says treat every property as possibly absent.
 *
 * `prepareDimensions` came along from `helper/ga-helper` because it is the only thing
 * `captureInterest` needed from it — it reads `businessStore` through core's own store.
 */

/**
 * GA custom dimensions describing the current user/branch. Reads the store at CALL time, never
 * at module scope — the rule that keeps the store<->helpers cycle survivable (ledger).
 *
 * ⚠️ Destructures `userData` and `businessData` WITHOUT guards, exactly as apps/web does. If
 * `businessStore` is not yet populated this throws — preserved verbatim rather than "fixed",
 * because adding guards would change when the caller's `catch` fires.
 */
export function prepareDimensions() {
  console.log("business reducer data", store.getState().businessStore);
  const {
    userData: { userId },
    businessData: { brandName, branchName, branchId, businessName },
  } = store.getState().businessStore;
  return {
    dimension1: userId,
    dimension2: branchId,
    dimension3: branchName,
    dimension4: brandName,
    dimension5: businessName,
  };
}

/**
 * Sends DotPe's enquiry (outside rista-be): a POST that creates it, then a PATCH adding who asks (`prepareDimensions`) and
 * `dataToSend` (such as `{ featureType }`). Answers the PATCH's `status`, false on a refusal or a failure (each toasted
 * through `notifyError`), and nothing when no address is configured. Web's body, kept as it was when shared.
 */
export async function captureInterest(dataToSend = {}) {
  const url = getEnv("dotpeApiUrl") + ENDPOINT.DOTPE.enquiry;
  if (!url) return;
  const body = {
    "name": "Rista Support",
    "phone": "8447902064",
    "email": "rista.support@dotpe.in",
    "source": getWindow()?.location?.href
  }
  const dimensionData = prepareDimensions();
  try {
    const response = await axios.post(url, body);
    const data: { id?: number; message?: string; status: boolean; } = response.data;
    if (!data.status) { notifyError('Something went wrong'); return false; }
    const enquiryApiResponse = await axios.patch(url + '/' + data.id, { extraInfo: { ...dimensionData, ...dataToSend } });
    const enquiryResponse: { message?: string; status: boolean; } = enquiryApiResponse.data;
    return enquiryResponse.status;
  }
  catch (e) {
    notifyError('something went wrong');
    return false;
  }
}
